/**
 * TableOfContents - Auto-generates a TOC from markdown headings
 *
 * Extracts h1 and h2 headings from the HTML AST and creates a
 * sticky sidebar navigation with proper nested lists.
 */

import { useEffect, useState } from "react"
import * as css from "./TableOfContents.module.css"

/**
 * Extract text content from a HAST node recursively
 */
function extractTextFromNode(node) {
  if (!node) return ""
  if (node.type === "text") return node.value || ""
  if (node.children) {
    return node.children.map(extractTextFromNode).join("")
  }
  return ""
}

/**
 * Extract headings from htmlAst (HAST format from gatsby-transformer-remark)
 * Only h1 and h2 to avoid too much granularity
 */
function extractHeadings(htmlAst) {
  const headings = []

  const traverse = (node) => {
    if (!node) return

    // Check if this is an h1 or h2 element
    if (
      node.type === "element" &&
      (node.tagName === "h1" || node.tagName === "h2")
    ) {
      const level = parseInt(node.tagName.charAt(1), 10)
      const id = node.properties?.id || ""
      const text = extractTextFromNode(node)

      if (text && id) {
        headings.push({ id, text: text.trim(), level })
      }
    }

    // Recursively traverse children
    if (node.children) {
      node.children.forEach(traverse)
    }
  }

  traverse(htmlAst)
  return headings
}

/**
 * Check if heading text starts with a section number (e.g., "1.", "2.", etc.)
 */
function startsWithNumber(text) {
  return /^\d+\./.test(text.trim())
}

/**
 * Build a tree structure from flat headings list
 * h1s become top-level items, h2s become children of the preceding h1
 * Special case: skip intro h1s (non-numbered) and promote their children
 */
function buildHeadingTree(headings) {
  const tree = []
  let currentParent = null

  const minLevel = Math.min(...headings.map((h) => h.level))

  headings.forEach((heading, index) => {
    const isTopLevel = heading.level === minLevel
    const isNumberedSection = startsWithNumber(heading.text)

    if (isTopLevel) {
      // Skip non-numbered intro headings (like "Welcome to the rabbit hole")
      // Their children will be promoted to top level
      if (!isNumberedSection && index === 0) {
        currentParent = null // Don't create a parent, children go to top level
        return
      }

      // Top-level numbered heading
      currentParent = { ...heading, children: [] }
      tree.push(currentParent)
    } else if (currentParent) {
      // Child heading - add to current parent
      currentParent.children.push(heading)
    } else {
      // No parent (intro was skipped), promote to top-level
      tree.push({ ...heading, children: [] })
    }
  })

  return tree
}

function TableOfContents({ htmlAst, title = "Contents" }) {
  const [activeId, setActiveId] = useState("")
  const headings = extractHeadings(htmlAst)
  const headingTree = buildHeadingTree(headings)

  useEffect(() => {
    // Don't set up observer if fewer than 2 headings
    if (headings.length < 2) return undefined

    // Set up intersection observer to highlight active section
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        })
      },
      {
        rootMargin: "-80px 0px -80% 0px",
        threshold: 0,
      }
    )

    // Observe all heading elements
    headings.forEach(({ id }) => {
      const element = document.getElementById(id)
      if (element) {
        observer.observe(element)
      }
    })

    return () => observer.disconnect()
  }, [headings])

  // Don't render if fewer than 2 headings
  if (headings.length < 2) {
    return null
  }

  const handleClick = (e, id) => {
    e.preventDefault()
    const element = document.getElementById(id)
    if (element) {
      const yOffset = -100 // Account for fixed header
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset
      window.scrollTo({ top: y, behavior: "smooth" })
      // Update URL hash without jumping
      window.history.pushState(null, "", `#${id}`)
      setActiveId(id)
    }
  }

  const renderItem = ({ id, text, children }) => (
    <li key={id} className={`${css.item} ${activeId === id ? css.active : ""}`}>
      <a href={`#${id}`} onClick={(e) => handleClick(e, id)}>
        {text}
      </a>
      {children && children.length > 0 && (
        <ul className={css.nestedList}>
          {children.map((child) => (
            <li
              key={child.id}
              className={`${css.item} ${css.nested} ${
                activeId === child.id ? css.active : ""
              }`}
            >
              <a
                href={`#${child.id}`}
                onClick={(e) => handleClick(e, child.id)}
              >
                {child.text}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  )

  return (
    <nav className={css.toc} aria-label="Table of contents">
      <h3 className={css.title}>{title}</h3>
      <ul className={css.list}>{headingTree.map(renderItem)}</ul>
    </nav>
  )
}

export default TableOfContents
